import { useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import type { Card } from '@/@types/Checkout.type';
import { CardInfo } from '@/components/organisms/checkoutV2/components/CardInfo';
import CreateCard from '@/pages/paymentMethods/components/organisms/CreateCard';
import { usePaymentCards } from '../hooks/usePaymentCards';

/**
 * The card that pays an upgrade's adjustment: a saved card, or a new one added
 * with the same form as «Métodos de pago». Only cards: the engine refuses any
 * other method for this charge.
 */
export const AdjustmentCardPicker = ({
	selectedCardId,
	onSelect,
	disabled,
}: {
	selectedCardId: string | null;
	onSelect: (card: Card) => void;
	disabled?: boolean;
}) => {
	const [isAddingCard, setIsAddingCard] = useState(false);
	const { query: cardsQuery, cards } = usePaymentCards();

	const handleCardAdded = async () => {
		const knownIds = new Set(cards.map((card) => card.id));
		const { data: fresh } = await cardsQuery.refetch();
		const added = (fresh ?? []).find((card) => !knownIds.has(card.id)) ?? fresh?.[0];
		if (added) onSelect(added);
		setIsAddingCard(false);
	};

	if (cardsQuery.isLoading) {
		return (
			<div className='flex justify-center py-4'>
				<Spinner className='size-5' />
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-3'>
			<p className='text-h-text-heading text-sm font-semibold'>Paga con tarjeta</p>
			{cards.length > 0 && (
				<RadioGroup
					value={selectedCardId ?? ''}
					onValueChange={(cardId) => {
						const card = cards.find((saved) => saved.id === cardId);
						if (card) onSelect(card);
					}}
					className='flex flex-col gap-2'>
					{cards.map((card) => (
						<label
							key={card.id}
							className='border-h-neutral-90 flex items-center gap-3 rounded-xl border bg-white px-3 py-2.5'>
							<RadioGroupItem value={card.id} disabled={disabled} aria-label={`Tarjeta terminada en ${card.details?.last_four}`} />
							<CardInfo card={card} hideExpireDate />
						</label>
					))}
				</RadioGroup>
			)}
			{isAddingCard || cards.length === 0 ? (
				<CreateCard onPaymentMethodSuccess={handleCardAdded} />
			) : (
				<button
					type='button'
					disabled={disabled}
					onClick={() => setIsAddingCard(true)}
					className='text-h-primary-50 self-start text-base font-semibold disabled:opacity-50'>
					Usar otra tarjeta
				</button>
			)}
		</div>
	);
};
